import type {ChangeEvent} from 'react'
import {useReducer} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {
  toHTMLTextDangerous,
  displayHTMLOrPlainTextInSpan,
} from '@freckle/educator-entities/ts/common/models/html-text'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {H1} from '@freckle/educator-materials/ts/components/typography'
import {ArticleReadingLevelDropDown} from '@freckle/classroom/ts/common/components/article-description/article-text-level-dropdown'
import type {
  State,
  FileLoadedState,
} from '@freckle/classroom/ts/ela/debugger/logic/ela-article-debugger'
import {parseYamlFile} from '@freckle/classroom/ts/ela/debugger/logic/ela-article-debugger'
import {ArticleDescriptionPreview} from './../../../common/components/article-description/article-description-preview'

type Message =
  | {tag: 'change-ela-article-level'; textLevel: string}
  | {tag: 'render-loaded-file'; state: FileLoadedState}

const reducer = (state: State, message: Message): State => {
  switch (message.tag) {
    case 'render-loaded-file':
      return {tag: 'file-loaded', ...message.state}
    case 'change-ela-article-level': {
      switch (state.tag) {
        case 'no-file-loaded':
          return state
        case 'file-loaded': {
          const currentArticleLevel = state.article.levels.find(
            level => level.freckleTextLevel === message.textLevel,
          )
          return currentArticleLevel === undefined ? state : {...state, currentArticleLevel}
        }
        default:
          return exhaustive(state)
      }
    }
    default:
      return exhaustive(message)
  }
}

export function ElaArticleDebugger({initialState}: {initialState?: State}) {
  const [state, dispatch] = useReducer(reducer, initialState ?? {tag: 'no-file-loaded'})

  const parseFile = (fileInputValue: Blob): Promise<void> =>
    parseYamlFile(fileInputValue).then(state => dispatch({tag: 'render-loaded-file', state}))

  const handleClickReload = () => {
    switch (state.tag) {
      case 'file-loaded':
        parseFile(state.fileInputValue)
        break
      case 'no-file-loaded':
        break
      default:
        exhaustive(state)
    }
  }

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null) {
      const fileInputValue = event.target.files[0]!
      parseFile(fileInputValue)
    }
  }

  const [articlePreview, articleInfo, reloadBtn] =
    state.tag === 'file-loaded'
      ? [
          <ArticleDescriptionPreview
            language={Languages.En}
            key="preview"
            article={state.article}
            // While Re-arch is happening we are not displaying Standards and skills
            vocabWords={[]}
            currentArticleLevel={state.currentArticleLevel}
            rlSkillToStandardMap={new Map()}
          />,
          <Flex key="info" alignItems="center" flexDirection="column">
            <H1>{displayHTMLOrPlainTextInSpan(toHTMLTextDangerous(state.article.title))}</H1>
            <img src={state.article.diagramUrl} />
          </Flex>,
          <Button key="reload-btn" onClick={handleClickReload}>
            Reload
          </Button>,
        ]
      : [null, null, null]

  const articleReadingLevelDropdown =
    state.tag === 'file-loaded' ? (
      <ArticleReadingLevelDropDown
        article={state.article}
        currentArticleLevel={state.currentArticleLevel}
        onSelectElaArticleLevelId={textLevel =>
          dispatch({tag: 'change-ela-article-level', textLevel})
        }
      />
    ) : null

  return (
    <>
      <Flex justifyContent="space-around" alignItems="center">
        <input type="file" onChange={handleChangeFile} data-test="yaml-file-input" />

        {articleReadingLevelDropdown}

        {reloadBtn}
      </Flex>

      {articleInfo}
      {articlePreview}
    </>
  )
}
